import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from 'config/axiosConfig';
import { transformHtmlContent } from 'util/transformHtmlContent';
import Report from 'components/Entities/Report';
import { 
  ChevronsDown,
  ChevronsUp,
  Clock, 
  Calendar, 
  MapPin, 
  UsersRound, 
  Video, 
  ExternalLink,
  Ellipsis,
  Pencil,
  Trash,
  Flag
} from 'lucide-react';
import EntityActions from 'components/Entities/EntityActions';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from 'ui/Carousel';
import {
  Chip,
  Tooltip
} from '@mui/joy';
import { Meeting } from 'util/constants';
import { formatDate, formatDateWithoutTimezone, formatRelativeTime } from 'config/dayjsConfig';
import { useUser } from 'util/UserContext';
import EntityAvatar from 'components/Entities/EntityAvatar';
import { getEntityDetails } from 'util/getObjectDetails';
import { 
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from 'ui/DropdownMenu';
import { Drawer, DrawerContent, DrawerTrigger } from 'ui/Drawer';
import DialogConfirmation from 'ui/DialogConfirmation';
import TextEditor from 'components/TextEditor';
import CoauthorSelect, { CoauthorSuggestion } from 'components/Coauthors/CoauthorSelect';
import DetailPanel from 'ui/DetailPanel';
import EntityPrivacy from 'components/Entities/EntityPrivacy';
import { PrivacyLevel } from 'util/userUtils';

interface Props {
  meeting: Meeting;
  showMeta?: boolean;
  truncateDescription?: boolean;
  isMeetingProfilePage?: boolean;
  isEntitySection?: boolean;
  showActions?: boolean;
  editMode?: boolean;
  onDescriptionChange?: (value: string) => void;
  onOrganizersChange?: (addedOrganizers: CoauthorSuggestion[], removedOrganizers: CoauthorSuggestion[]) => void;
  children?: React.ReactNode;
}

const MeetingCard = ({
  meeting,
  showMeta = true,
  truncateDescription = true,
  isMeetingProfilePage = false,
  isEntitySection = false,
  showActions = true,
  editMode = false,
  onDescriptionChange,
  onOrganizersChange,
  children,
}: Props) => {
  const { currentUser, setShowAuthModal, userIsMobile } = useUser();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const meetingHref = `/event/${encodeURIComponent(meeting.id)}`;
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);  
  const [existingOrganizers, setExistingOrganizers] = useState<CoauthorSuggestion[]>([]);
  const [newOrganizers, setNewOrganizers] = useState<CoauthorSuggestion[]>([]);
  const [removedOrganizers, setRemovedOrganizers] = useState<CoauthorSuggestion[]>([]);
  const [pendingOrganizers, setPendingOrganizers] = useState<CoauthorSuggestion[]>([]);
  const [isLoadingOrganizers] = useState<boolean>(false);
  const [isParticipant, setIsParticipant] = useState<boolean>(false);
  const [isOrganizer, setIsOrganizer] = useState<boolean>(false);
  const [participantCount, setParticipantCount] = useState<number>(0);
  const TruncateMarkup = require('react-truncate-markup').default;
  const [entityDetails, setEntityDetails] = useState<any>(null);
  const [isLoadingEntityDetails, setIsLoadingEntityDetails] = useState<boolean>(false);
 
  useEffect(() => {
    // Check if current user is participant or organizer
    if (meeting.id && currentUser) {
      const checkStatusPromises = [
        api.get(`/api/meetings/${meeting.id}/participant-status`).then(res => res.data.isParticipant),
        api.get(`/api/meetings/${meeting.id}/organizer-status`).then(res => res.data.isOrganizer)
      ];
      
      Promise.all(checkStatusPromises)
        .then(([participantStatus, organizerStatus]) => {
          setIsParticipant(participantStatus);
          setIsOrganizer(organizerStatus);
        })
        .catch(err => console.error('Error checking participant/organizer status:', err));
      
      // Get participant count
      api.get(`/api/meetings/${meeting.id}/participants`)
        .then(res => setParticipantCount(res.data.total))
        .catch(err => console.error('Error getting participant count:', err));
    }
    
    // Load organizers
    if (meeting.properties?.organizers) {
      const organizers = meeting.properties.organizers.filter(
        (org: any) => org.status === 'active'
      ).map((org: any) => ({
        id: org.id,
        type: org.type,
        status: org.status
      }));
      
      const pendingOrgs = meeting.properties.organizers.filter(
        (org: any) => org.status === 'pending'
      ).map((org: any) => ({
        id: org.id,
        type: org.type,
        status: org.status
      }));
      
      setExistingOrganizers(organizers);
      setPendingOrganizers(pendingOrgs);
    }

    // Load entity details
    const fetchEntityDetails = async () => {
      if (meeting.properties?.entity_type && meeting.properties?.entity_id) {
        setIsLoadingEntityDetails(true);
        try {
          const details = await getEntityDetails(meeting.properties.entity_type, meeting.properties.entity_id);
          setEntityDetails(details);
        } catch (error) {
          console.error('Error fetching entity details:', error);
        } finally {
          setIsLoadingEntityDetails(false);
        }
      }
    };

    fetchEntityDetails();

  }, [meeting.id, meeting.properties?.organizers, currentUser, meeting.properties?.entity_type, meeting.properties?.entity_id]);

  const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const element = event.target as HTMLElement;
    if (element.tagName.toLowerCase() === 'a') return;    
    if (element.classList.contains('read-more')) return;    
    if (!isMeetingProfilePage) {
      event.stopPropagation();
      navigate(meetingHref);
    }
  };

  const handleDescriptionClick = (event: React.MouseEvent) => {
    if (!truncateDescription || !isTruncated) return;
    event.preventDefault();
    if (isMeetingProfilePage && truncateDescription) {
      setIsExpanded(!isExpanded);
    } else {
      return;
    }
  };

  const handleReadMoreClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setIsExpanded(!isExpanded);
    event.stopPropagation(); // Prevent the click from bubbling up to the card
  };

  const handleReportClick = () => {
    if (!currentUser) {
      setShowAuthModal(true);
    } else {
      setMenuOpen(false);
      setDrawerOpen(false);
      setIsReportModalOpen(true);
    }
  };

  const handleEdit = () => {
    setMenuOpen(false);
    setDrawerOpen(false);
    navigate(`/event/${meeting.id}?edit=true`);
  };

  const handleDelete = () => {
    setMenuOpen(false);
    setDrawerOpen(false);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/api/meetings/${meeting.id}`);
      setIsDeleteDialogOpen(false);
      if (isMeetingProfilePage) {
        navigate('/');
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error deleting meeting:', error);
    }
  };

  const cancelDelete = () => {
    setIsDeleteDialogOpen(false);
  };
  
  const handleJoinRequest = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }
    
    try {
      await api.post(`/api/meetings/${meeting.id}/participants`);
      setIsParticipant(true);
    } catch (error) {
      console.error('Error joining meeting:', error);
    }
  };

  const handleCancelParticipation = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }
    
    try {
      await api.delete(`/api/meetings/${meeting.id}/participants/${currentUser.id}`);
      setIsParticipant(false);
    } catch (error) {
      console.error('Error canceling participation:', error);
    }
  };

  const handleOrganizerSelect = (suggestion: CoauthorSuggestion) => {
    if (
      !existingOrganizers.some(c => c.id === suggestion.id && c.type === suggestion.type) &&
      !newOrganizers.some(c => c.id === suggestion.id && c.type === suggestion.type)
    ) {
      setNewOrganizers([...newOrganizers, suggestion]);
      
      if (onOrganizersChange) {
        onOrganizersChange([...newOrganizers, suggestion], removedOrganizers);
      }
    }
  };

  const handleOrganizerRemove = (organizer: CoauthorSuggestion) => {
    // If it's an existing organizer, add to removed list
    if (existingOrganizers.some(c => c.id === organizer.id && c.type === organizer.type)) {
      setRemovedOrganizers([...removedOrganizers, organizer]);
      setExistingOrganizers(existingOrganizers.filter(
        c => !(c.id === organizer.id && c.type === organizer.type)
      ));
    } 
    // If it's a new organizer, just remove from new list
    else if (newOrganizers.some(c => c.id === organizer.id && c.type === organizer.type)) {
      setNewOrganizers(newOrganizers.filter(
        c => !(c.id === organizer.id && c.type === organizer.type)
      ));
    }
    
    if (onOrganizersChange) {
      onOrganizersChange(
        newOrganizers.filter(c => !(c.id === organizer.id && c.type === organizer.type)),
        [...removedOrganizers, organizer]
      );
    }
  };

  const formatLocation = (place: any) => {
    if (place.is_virtual) {
      const url = place.location?.event_url;
      if (!url) return 'Virtual meeting (no link provided)';
      
      return (
        <div className="flex items-center gap-1 ">
          <Video className="h-4 w-4" />
          <a 
            href={url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="external-link hover:underline flex items-center"
            onClick={(e) => e.stopPropagation()}
          >
            Online <ExternalLink className="h-3 w-3 ml-1" />
          </a>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-1">
          <MapPin className="h-4 w-4" />
          <span>{place.location?.address || 'In-person meeting (no address provided)'}</span>
        </div>
      );
    }
  };

  const renderDescription = () => {
    if (truncateDescription && !isExpanded) {
      return (
        <TruncateMarkup lines={10} tokenize="words" ellipsis={truncateEllipsis} onTruncate={(wasTruncated: any) => setIsTruncated(wasTruncated)}>
          <div>
            {transformHtmlContent(meeting.description || '')}
          </div>
        </TruncateMarkup>
      );
    } else {
      return (
        <div>
          {transformHtmlContent(meeting.description || '')}
          {isTruncated && (
            <>
              <span className="flex">
                <button 
                  className="read-more-border w-full text-sm rounded-md border-b-2 justify-center items-center px-1"
                  onClick={handleReadMoreClick}
                >
                  <span className="flex justify-center items-center">
                    <ChevronsUp className="flex justify-center items-center" />
                  </span>
                </button>
              </span>
            </>
          )}
        </div>
      );
    }
  };

  // For truncate/expand description
  const truncateEllipsis = (node: any) => {
    return (
      <span className="read-more">
        <button 
          className="read-more-border w-full text-sm rounded-md border-b-2 justify-center items-center px-1"
          onClick={handleReadMoreClick}
        >
          <span className="flex justify-center items-center">
            <ChevronsDown className="flex justify-center items-center"/>
          </span>
        </button>
      </span>
    );
  };

  // For mobile dropdown/drawer
  const MoreButton = React.forwardRef<
    HTMLButtonElement,
    React.ButtonHTMLAttributes<HTMLButtonElement>
  >((props, ref) => (
    <button
      type="button"
      ref={ref}
      {...props}
      className="card-more size-7 rounded-full flex justify-center items-center"
    >
      <Ellipsis className="icon-stroke size-5"/>
    </button>
  ));
  MoreButton.displayName = 'MoreButton';

  const MoreOptions = () => (
    <>
      {!userIsMobile ? (
        <div className="flex flex-col">
          {currentUser?.id === meeting.user_id && (
            <>
              <DropdownMenuItem onClick={handleEdit}>
                <div className="flex items-center gap-2 px-4 py-2 rounded-md text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                  <Pencil className="size-4 text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke"/>
                  <span>Edit</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDelete}>
                <div className="flex items-center gap-2 px-4 py-2 rounded-md text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                  <Trash className="size-4 text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke"/>
                  <span>Delete</span>
                </div>
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuItem onClick={handleReportClick}>
            <div className="flex items-center gap-2 px-4 py-2 rounded-md text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
              <Flag className="size-4 text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke"/>
              <span>Report</span>
            </div>
          </DropdownMenuItem>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {currentUser?.id === meeting.user_id && (
            <>
              <button onClick={handleEdit} className="flex mt-2 w-full items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                <Pencil className="size-4"/>
                <span>Edit</span>
              </button>
              <button onClick={handleDelete} className="flex w-full items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                <Trash className="size-4"/>
                <span>Delete</span>
              </button>
            </>
          )}
          <button onClick={handleReportClick} className="flex w-full items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
            <Flag className="size-4"/>
            <span>Report</span>
          </button>
        </div>
      )}
    </>
  );

  return (
    <div className="card-container my-1">
      {isReportModalOpen && (
        <Report
          entityId={meeting.id}
          entityType="meeting"
          open={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
        />
      )}
      <article>
        <div className={`rounded-lg flex flex-col px-2 md:px-3 py-2 relative ${isMeetingProfilePage ? 'card-main-profile-page' : 'card-main'}`}>
          {showMeta && (
            <div className="entity-actions-container order-last">
              <EntityActions entity={meeting} entityType="meeting" />
            </div>
          )}
          <div className="card-content order-first">
            {showMeta && (
              <div className="card-meta text-sm flex items-center justify-between relative">
                {isLoadingEntityDetails ? (
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-full bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover animate-pulse"></div>
                    <div className="h-4 w-24 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover animate-pulse rounded"></div>
                  </div>
                ) : meeting.properties?.entity_type && meeting.properties?.entity_id && entityDetails ? (
                  <EntityAvatar 
                    entity={entityDetails}
                    entityType={entityDetails.type}
                    isEntityProfilePage={isMeetingProfilePage}
                    isEntitySection={isEntitySection}
                    isGeneralEntity={true}
                    badgeText={entityDetails.type.charAt(0).toUpperCase() + entityDetails.type.slice(1)}
                  />
                ) : (
                  <div className="text-eoe-text-sub dark:text-eoe-dark-text-sub text-sm">
                    <span>Event</span>
                  </div>
                )}
                <div className="icon-more text-xl">
                  {userIsMobile ? (
                    <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
                      <DrawerTrigger asChild>
                        <MoreButton />
                      </DrawerTrigger>
                      <DrawerContent title="Actions">
                        <MoreOptions />
                      </DrawerContent>
                    </Drawer>
                  ) : (
                    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
                      <DropdownMenuTrigger asChild>
                        <MoreButton />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <MoreOptions />
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            )}
            <div
              className="card-header my-1"
              onClick={handleCardClick}
            >
              {isMeetingProfilePage ? (
                <span className="card-title">
                  {meeting.title}
                </span>
              ) : (
                <Link to={meetingHref} className="card-title">
                  {meeting.title}
                </Link>
              )}
              <div className="card-tags flex flex-row flex-wrap gap-2 my-2 py-2 items-center">
                {/* Event Date/Time */}
                <div className="flex p-1 px-2 items-center text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke bg-eoe-icon dark:bg-eoe-dark-icon rounded-full">
                  <Calendar className="h-4 w-4 mr-1" />
                  <span className="card-date text-xs font-normal text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke">
                    {formatDateWithoutTimezone(meeting.properties?.start_at || meeting.created_at)}
                  </span>
                </div>
                
                {meeting.properties?.is_private && (
                  <Chip 
                    component={'div' as any}
                    variant="soft" 
                    color="warning" 
                    size="sm"
                  >
                    Private Event
                  </Chip>
                )}
                
                <EntityPrivacy
                  privacy={meeting.properties_public?.privacy as PrivacyLevel}
                  viewerRelation={meeting.viewer_relation}
                  onPrivacyChange={() => {}}
                  actorType="user"
                  editMode={false}
                  entityId={meeting.id}
                  entityType="meeting"
                  miniIcon={true}
                  showDropdown={false}
                />
                <Tooltip title={formatDate(meeting.created_at)} arrow>
                  <span className="card-date font-normal">
                    {formatRelativeTime(meeting.created_at)}
                  </span>
                </Tooltip>
              </div>
            </div>
            
            {/* Event Details */}
            <div className="flex flex-col space-y-2 text-sm mb-3 border border-eoe-border dark:border-eoe-dark-border rounded-md p-2">
              <div className="flex items-center text-xs">
                <Clock className="h-4 w-4 mr-2 text-eoe-text-sub dark:text-eoe-dark-text-sub" />
                <span>
                  {formatDateWithoutTimezone(meeting.properties?.start_at || meeting.created_at)}
                  {meeting.properties?.end_at && ` - ${formatDateWithoutTimezone(meeting.properties.end_at)}`}
                </span>
              </div>
              {meeting.properties?.places && meeting.properties.places.length > 0 && (
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-2 text-eoe-text-sub dark:text-eoe-dark-text-sub" />
                  {meeting.properties.places.length === 1 ? (
                    formatLocation(meeting.properties.places[0])
                  ) : (
                    <Carousel className="w-64">
                      <CarouselContent>
                        {meeting.properties.places.map((place: any, index: number) => (
                          <CarouselItem key={index}>
                            {formatLocation(place)}
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                      <CarouselPrevious className="h-6 w-6 left-0" />
                      <CarouselNext className="h-6 w-6 right-0" />
                    </Carousel>
                  )}
                </div>
              )}
              <div className="flex items-center">
                <UsersRound className="h-4 w-4 mr-2 text-eoe-text-sub dark:text-eoe-dark-text-sub" />
                <span>{participantCount} participant{participantCount !== 1 ? 's' : ''}</span>
                
                {currentUser && !isParticipant && !isOrganizer && (
                  <button 
                    onClick={handleJoinRequest}
                    className="ml-4 text-blue-500 hover:text-blue-700 text-xs"
                  >
                    Join
                  </button>
                )}
                
                {currentUser && isParticipant && (
                  <button 
                    onClick={handleCancelParticipation}
                    className="ml-4 text-red-500 hover:text-red-700 text-xs"
                  >
                    Cancel participation
                  </button>
                )}
              </div>
            </div>

            {/* Description and Organizers */}
            <div className="flex flex-col-reverse md:grid md:grid-cols-3 md:grid-flow-row gap-2">
              {!editMode ? (
                <>
                  <div className="card-description md:col-span-2" onClick={handleCardClick}>
                    <span onClick={handleDescriptionClick}>
                      {renderDescription()}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(existingOrganizers.length || pendingOrganizers.length) > 0 &&
                      <DetailPanel 
                        title={`Organizers (${existingOrganizers.length + pendingOrganizers.length})`} 
                        className="text-sm bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal">
                        <Carousel className="border-none border-0">
                          <div className="flex justify-between ">
                            <>
                              <CarouselContent>
                                {existingOrganizers.map((organizer) => (
                                  <CarouselItem key={`existing-${organizer.type}-${organizer.id}`} className="border-none basis-auto">
                                    <EntityAvatar
                                      key={`existing-${organizer.type}-${organizer.id}`}
                                      entity={organizer as any}
                                      entityType={organizer.type}
                                      entityChip={true}
                                      showCloseChip={false}
                                      isInputField={false}
                                      showLink={true}
                                    />
                                  </CarouselItem>
                                ))}
                                {pendingOrganizers.map((organizer) => (
                                  <CarouselItem key={`pending-${organizer.type}-${organizer.id}`} className="border-none basis-auto">
                                    <EntityAvatar
                                      key={`pending-${organizer.type}-${organizer.id}`}
                                      entity={organizer as any}
                                      entityType={organizer.type}
                                      entityChip={true}
                                      showCloseChip={false}
                                      isInputField={false}
                                      badgeElement={<Clock className="size-3.5 text-yellow-500 dark:text-yellow-300" />}
                                      showLink={true}
                                    />
                                  </CarouselItem>
                                ))}
                              </CarouselContent>
                              <div className="flex flex-col justify-between">
                                <CarouselNext className="flex" size={'circle'}/>
                                <CarouselPrevious className="flex" size={'circle'}/>
                              </div>
                            </>
                          </div>
                        </Carousel>
                      </DetailPanel>
                    }
                  </div>
                </>
              ) : (
                <>
                  <div className="flex flex-wrap w-full justify-between items-center gap-2 mt-0.5">
                    <div className="flex-shrink-0">
                      <EntityPrivacy
                        privacy={meeting.properties_public?.privacy as PrivacyLevel}
                        onPrivacyChange={() => {}}
                        editMode={true}
                        entityId={meeting.id}
                        entityType="meeting"
                      />
                    </div>
                    <div className="flex flex-wrap gap-1 items-center">
                      {children}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 md:col-span-2">
                    <TextEditor
                      value={meeting.description || ''}
                      onChange={(value) => onDescriptionChange?.(value)}
                      editorType="entity"
                      placeholder="Description"
                      isEditorExpanded={true}
                      action="edit"
                    />
                  </div>
                  <DetailPanel 
                    title={`Organizers (${existingOrganizers.length + pendingOrganizers.length})`} 
                    className="mb-2 text-sm bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal">
                    {isLoadingOrganizers ? (
                      <div className="p-2 text-center">Loading organizers...</div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex flex-col gap-2">
                          <CoauthorSelect
                            inputType="add"
                            placeholder="Add organizers"
                            className="w-full"
                            onSelect={handleOrganizerSelect}
                            excludeIds={[
                              ...existingOrganizers.map(c => c.id),
                              ...newOrganizers.map(c => c.id)
                            ]}
                          />
                        </div>
                        {existingOrganizers.map((organizer) => (
                          <EntityAvatar
                            key={`existing-${organizer.type}-${organizer.id}`}
                            entity={organizer as any}
                            entityType={organizer.type}
                            entityChip={true}
                            showCloseChip={editMode}
                            isInputField={false}
                            showLink={false}
                            onEntityChipClose={() => handleOrganizerRemove(organizer)}
                          />
                        ))}
                        {newOrganizers.map((organizer) => (
                          <EntityAvatar
                            key={`new-${organizer.type}-${organizer.id}`}
                            entity={organizer as any}
                            entityType={organizer.type}
                            entityChip={true}
                            showCloseChip={editMode}
                            isInputField={false}
                            showLink={false}
                            onEntityChipClose={() => handleOrganizerRemove(organizer)}
                          />
                        ))}
                      </div>
                    )}
                  </DetailPanel>
                </>
              )}
            </div>

            {children}
          </div>
        </div>
      </article>
      
      {/* Delete Confirmation Dialog */}
      {isDeleteDialogOpen && (
        <DialogConfirmation 
          title="Delete Event" 
          description="Are you sure you want to delete this event? This action cannot be undone."
          isOpen={isDeleteDialogOpen} 
          onConfirm={confirmDelete} 
          onCancel={cancelDelete}
        />
      )}
    </div>
  );
};

export default MeetingCard;
